// 6th form
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { Image as ImageIcon, Loader2, ShieldAlert, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Trash2, FileText } from 'lucide-react';
import { registerSupplierAction } from '../../../reduxstore/actions/authActions';
import { LockClosedIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
];

const DOCUMENT_TITLES = {
  qualityCertificate_one: 'Quality Certificate 1',
  qualityCertificate_two: 'Quality Certificate 2',
  qualityCertificate_three: 'Quality Certificate 3',
  qualityCertificate_four: 'Quality Certificate 4',
  hipaaCertificate_one: 'HIPAA Certificate 1',
  hipaaCertificate_two: 'HIPAA Certificate 2',
  hipaaCertificate_three: 'HIPAA Certificate 3',
  hipaaCertificate_four: 'HIPAA Certificate 4',
  regulatoryCertificate_one: 'Regulatory Certificate 1',
  regulatoryCertificate_two: 'Regulatory Certificate 2',
  regulatoryCertificate_three: 'Regulatory Certificate 3',
  regulatoryCertificate_four: 'Regulatory Certificate 4',
  environmentalCertificate_one: 'Environmental Certificate 1',
  environmentalCertificate_two: 'Environmental Certificate 2',
  environmentalCertificate_three: 'Environmental Certificate 3',
  environmentalCertificate_four: 'Environmental Certificate 4',
};

const UploadSpecificCert1 = ({ setSectionId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);

  const reduxData = useSelector(
    (state) => state?.authReducer?.registrationData,
  );
  const token = useSelector((state) => state.authReducer.token);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const [documentStates, setDocumentStates] = useState({
    qualityCertificate_one: reduxData?.qualityCertificate_one || null,
    qualityCertificate_two: reduxData?.qualityCertificate_two || null,
    qualityCertificate_three: reduxData?.qualityCertificate_three || null,
    qualityCertificate_four: reduxData?.qualityCertificate_four || null,
    hipaaCertificate_one: reduxData?.hipaaCertificate_one || null,
    hipaaCertificate_two: reduxData?.hipaaCertificate_two || null,
    hipaaCertificate_three: reduxData?.hipaaCertificate_three || null,
    hipaaCertificate_four: reduxData?.hipaaCertificate_four || null,
    regulatoryCertificate_one: reduxData?.regulatoryCertificate_one || null,
    regulatoryCertificate_two: reduxData?.regulatoryCertificate_two || null,
    regulatoryCertificate_three: reduxData?.regulatoryCertificate_three || null,
    regulatoryCertificate_four: reduxData?.regulatoryCertificate_four || null,
    environmentalCertificate_one:
      reduxData?.environmentalCertificate_one || null,
    environmentalCertificate_two:
      reduxData?.environmentalCertificate_two || null,
    environmentalCertificate_three:
      reduxData?.environmentalCertificate_three || null,
    environmentalCertificate_four:
      reduxData?.environmentalCertificate_four || null,
  });
  // Initialize states from redux data
  const [uploadProgress, setUploadProgress] = useState({
    qualityCertificate_one: 0,
    qualityCertificate_two: 0,
    qualityCertificate_three: 0,
    qualityCertificate_four: 0,
    hipaaCertificate_one: 0,
    hipaaCertificate_two: 0,
    hipaaCertificate_three: 0,
    hipaaCertificate_four: 0,
    regulatoryCertificate_one: 0,
    regulatoryCertificate_two: 0,
    regulatoryCertificate_three: 0,
    regulatoryCertificate_four: 0,
    environmentalCertificate_one: 0,
    environmentalCertificate_two: 0,
    environmentalCertificate_three: 0,
    environmentalCertificate_four: 0,
  });

  // Fetch existing documents on component mount
  useEffect(() => {
    fetchExistingDocuments();
  }, [token]);

  // 1. Fetch existing Doucments to hydrate the state

  const fetchExistingDocuments = async () => {
    try {
      const response = await apiClient.get('/api/documents/get');

      if (response.status === 200) {
        setExistingDocuments(response.data.data || []);
      }
      if (response.status === 401) {
        setError('Unauthorized');
      }
      if (response.status === 500) {
        setError('Internal Server Error');
        console.log('Internal Server Error');
      }
      if (response.status !== 200) {
        setError('Failed to fetch documents');
      }
    } catch (err) {
      console.error(
        'Error fetching documents:',
        err.response.data.error_message,
      );
      setError(
        `Unauthorized! ${err.response.data.error_message}` ||
          'Failed to fetch documents',
      );
    }
  };

  // 2. Function to get presigned URL and open document preview
  const fetchPreviewUrl = async (uniqueKey) => {
    try {
      const response = await apiClient.get('/api/documents/presigned-url', {
        headers: {
          'unique-key': uniqueKey,
        },
      });

      const result = response.data;
      if (result.data) {
        window.open(result.data, '_blank');
      }
    } catch (error) {
      setError('Failed to fetch preview URL');
      console.error('Error fetching preview URL:', error);
      throw new Error('Failed to fetch preview URL');
    }
  };

  // 3. Function to delete a document
  const handleDeleteDocument = async (uniqueKey) => {
    try {
      const response = await apiClient.delete('/api/documents/delete', {
        headers: {
          'unique-key': uniqueKey,
        },
      });

      // Find the document being deleted
      const deletedDoc = existingDocuments.find(
        (doc) => doc.document_unique_key === uniqueKey,
      );

      await fetchExistingDocuments();
      // Update existing documents state
      setExistingDocuments((prev) =>
        prev.filter((doc) => doc.document_unique_key !== uniqueKey),
      );

      // Update local document state
      if (deletedDoc) {
        setDocumentStates((prev) => ({
          ...prev,
          [deletedDoc.document_name]: null,
        }));

        // Update Redux state to remove the document
        dispatch(
          registerSupplierAction({
            [deletedDoc.document_name]: null,
          }),
        );
      }
    } catch (error) {
      const error_message = error?.response?.data?.error_message;
      console.error('Error deleting document: ', error_message);
      setError(error_message || 'Failed to delete document');
      throw new Error(error_message || 'Failed to delete document');
    }
  };

  // 4. Function to handle individual file upload
  const handleIndividualUpload = async (file, documentName) => {
    setIsUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document-name', documentName);

      const uploadResponse = await apiClient.post(
        '/api/documents/upload',
        formData,
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            console.log(
              `Upload progress for ${documentName}:`,
              Math.round((progressEvent.loaded / progressEvent.total) * 100),
            );
            setUploadProgress((prev) => ({
              ...prev,
              [documentName]: progressEvent.total
                ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
                : 0,
            }));
          },
        },
      );

      if (uploadResponse.status !== 200) {
        throw new Error('Failed to upload files');
      }

      const uploadResult = uploadResponse.data;
      if (!uploadResult.success) {
        throw new Error(uploadResult.error_message || 'File upload failed');
      }

      const newDocumentData = uploadResult.data;

      await fetchExistingDocuments();
      // Update existing documents state
      setExistingDocuments((prev) => [
        ...prev.filter((doc) => doc.document_name !== documentName),
        newDocumentData,
      ]);

      // Update local state
      setDocumentStates((prev) => ({
        ...prev,
        [documentName]: file,
      }));

      // Dispatch Redux action with presigned URL
      dispatch(
        registerSupplierAction({
          [documentName]: newDocumentData.presigned_url,
        }),
      );
      // if upload is successful, put the progress to 100%
      setUploadProgress((prev) => ({
        ...prev,
        [documentName]: 100,
      }));
    } catch (err) {
      console.error(`Error uploading ${documentName}:`, err);
      setError(err.response.data.error_message || 'Failed to upload document');
    } finally {
      setIsUploading(false);
      // setProcessing((prev) => ({ ...prev, [documentType]: false }));

      // After a sec take the progress bar to 0
      setTimeout(() => {
        setUploadProgress((prev) => ({
          ...prev,
          [documentName]: 0,
        }));
      }, 300);
    }
  };

  // 5. Function to handle file upload
  const onSubmit = (data) => {
    dispatch(
      registerSupplierAction({
        ...reduxData,
        ...documentStates,
      }),
    );
    console.log('Data:', data);
    setSectionId(7);
  };
  // File upload component
  const FileUploadBox = React.memo(
    ({ id, label, documentType, existingDocument }) => {
      const [error, setError] = useState('');
      const [isDeleting, setIsDeleting] = useState(false);
      const file = documentStates[documentType];
      const progress = uploadProgress[documentType];
      const [displayProgress, setDisplayProgress] = useState(0);
      const animationFrameRef = useRef(null);
      const startProgressRef = useRef(0);
      const targetProgressRef = useRef(0);
      const startTimeRef = useRef(null);

      useEffect(() => {
        // Cancel any existing animation
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        // Set current display progress as start, new progress as target
        startProgressRef.current = displayProgress;
        targetProgressRef.current = progress;
        startTimeRef.current = performance.now();

        const animate = (currentTime) => {
          const elapsed = currentTime - startTimeRef.current;
          const duration = 500; // 500ms animation duration

          // Calculate progress using easing function (ease in-out)
          const t = Math.min(elapsed / duration, 1);
          const easedT =
            t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

          const newProgress =
            startProgressRef.current +
            (targetProgressRef.current - startProgressRef.current) * easedT;

          setDisplayProgress(newProgress);

          // Continue animation if not complete
          if (elapsed < duration) {
            animationFrameRef.current = requestAnimationFrame(animate);
          } else {
            // Ensure we end exactly at the target progress
            setDisplayProgress(targetProgressRef.current);
          }
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        // Cleanup
        return () => {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
        };
      }, [progress]);

      const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
          if (selectedFile.size > MAX_FILE_SIZE) {
            setError('File size exceeds 1MB');
            return;
          }
          if (!ACCEPTED_FILE_TYPES.includes(selectedFile.type)) {
            setError('Invalid file type');
            return;
          }

          // Trigger individual upload
          handleIndividualUpload(selectedFile, documentType);
        }
      };

      const handleRemove = useCallback(async (e) => {
        e.stopPropagation(); // Prevent click event from reaching the parent container

        setIsDeleting(true);
        if (existingDocument) {
          try {
            await handleDeleteDocument(existingDocument.document_unique_key);
          } catch (error) {
            console.error('Error deleting document:', error);
          } finally {
            setIsDeleting(false);
          }

          return;
        }

        // Reset document state for locally added files
        setDocumentStates((prev) => ({
          ...prev,
          [documentType]: null,
        }));

        // Update Redux state
        dispatch(
          registerSupplierAction({
            [documentType]: null,
          }),
        );
        setIsDeleting(false);
      });

      const renderFilePreview = (fileToPreview) => {
        if (fileToPreview?.type?.startsWith('image/')) {
          return (
            // <img
            //   src={URL.createObjectURL(fileToPreview)}
            //   alt={fileToPreview.name}
            //   className="w-full h-full object-cover rounded-md"
            // />
            <Image className="h-8 w-8 text-gray-400" />
          );
        } else if (fileToPreview?.type === 'application/pdf') {
          return (
            // <iframe
            //   src={URL.createObjectURL(fileToPreview)}
            //   title={fileToPreview.name}
            //   className="w-full h-full object-cover rounded-md"
            // ></iframe>

            <FileText className="h-8 w-8 text-gray-400" />
          );
        } else {
          return <ImageIcon className="h-8 w-8 text-gray-400" />;
        }
      };

      return (
        <>
          <div>
            <div className="relative group justify-center items-center flex flex-col">
              {file || existingDocument ? (
                <TooltipProvider>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <div
                        className="relative flex flex-col justify-center items-center w-36 h-28 border border-lightPurple hover:border-lightPurple ease-in duration-150 hover:border-opacity-70 border-opacity-30 rounded-md cursor-pointer overflow-hidden"
                        onClick={() => {
                          if (existingDocument) {
                            fetchPreviewUrl(
                              existingDocument.document_unique_key,
                            );
                          } else if (file) {
                            const fileURL = URL.createObjectURL(file);
                            window.open(fileURL, '_blank');
                          }
                        }}
                      >
                        <div className="w-full h-full flex items-center justify-center">
                          {file ? (
                            renderFilePreview(file)
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FileText className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-center p-2 truncate max-w-full absolute bottom-0 w-full bg-lightPurple bg-opacity-60 text-white backdrop-filter backdrop-blur-sm">
                          {existingDocument
                            ? DOCUMENT_TITLES[existingDocument.document_name] ||
                              existingDocument.document_name
                            : file.name}
                        </span>

                        {/* Trash Icon for existing documents */}
                        {(file || existingDocument) && (
                          <button
                            type="button"
                            onClick={handleRemove}
                            disabled={isDeleting}
                            className="absolute top-1 right-1 bg-purple-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          >
                            {isDeleting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-gray-100 bg-opacity-45 p-1 backdrop-filter backdrop-blur-sm rounded-md shadow-md translate-y-2">
                      <p className="text-center text-xs text-gray-600">
                        Click to preview
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <>
                  <Label
                    htmlFor={id}
                    className="w-36 h-28 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer text-muted-foreground hover:text-darkPurple hover:border-darkerPurple ease-in duration-200"
                  >
                    {isUploading && progress ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-center">Uploading</span>
                        {/* upload progress bar for the file that is being uploaded */}
                        {progress > 0 && (
                          <div className="relative w-3/4 justify-center h-1 bg-gray-200 rounded-md mt-2 ">
                            <div
                              className="absolute h-1 bg-lightPurple rounded-md transition-all duration-300"
                              style={{ width: `${displayProgress}%` }}
                            ></div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 mx-2 w-4" />
                        <span className="text-xs p-2 text-center">{label}</span>
                        <Input
                          id={id}
                          type="file"
                          className="hidden"
                          accept={ACCEPTED_FILE_TYPES.join(',')}
                          onChange={handleFileChange}
                        />
                      </>
                    )}
                  </Label>
                </>
              )}

              {error && (
                <span className="text-red-500 text-sm mt-1 text-center">
                  {error}
                </span>
              )}
            </div>
          </div>
        </>
      );
    },
  );
  return (
    <div className="w-full bg-white p-8 px-28 rounded-md font-roboto text-gray-700">
      <h2 className="text-3xl font-semibold text-black text-center mb-4">
        Upload Specific Certificates
      </h2>
      <p className="text-center text-[#637381] mb-8 text-base">
        Please add specific information to impress our buyers with your capacity
        and capabilities
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        className="space-y-6 flex flex-col"
      >
        <div className=" flex flex-col gap-4 justify-between items-between">
          {[
            { key: 'qualityCertificates', label: 'Quality Certificates' },
            { key: 'hipaaCertificate', label: 'HIPAA Certificates' },
            { key: 'regulatoryCertificates', label: 'Regulatory Certificates' },
            {
              key: 'environmentalCertificates',
              label: 'Environmental Certificates',
            },
          ].map(({ key, label }) => (
            <div key={key} className="certificate-section">
              <h3 className="text-lg font-semibold mb-4">{label}</h3>
              <div className="flex gap-4 justify-between">
                {[0, 1, 2, 3].map((index) => {
                  const documentType = `${key}-${index + 1}`;
                  const existingKey = existingDocuments.find(
                    (doc) => doc.document_name === documentType,
                  );

                  return (
                    <FileUploadBox
                      key={documentType}
                      id={documentType}
                      label={`${label.replace(' Certificates', '')} Certificate ${index + 1}`}
                      documentType={documentType}
                      existingDocument={existingKey}
                      onFileUpload={(file) => {
                        setDocumentStates((prev) => {
                          const updatedCertificates = [...prev[key]];
                          updatedCertificates[index] = file;
                          return {
                            ...prev,
                            [key]: updatedCertificates,
                          };
                        });
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-8 text-lg">
          <Button
            type="button"
            variant="outline"
            onClick={() => setSectionId(5)}
            className="px-4 border border-gray-300 py-2 h-12 w-36 hover:bg-slate-200"
          >
            Back
          </Button>
          <Button
            onClick={() => setSectionId(6)}
            className="px-4 py-2 h-12 w-36 bg-darkPurple hover:bg-darkerPurple ease-in duration-300"
            disabled={processing || isUploading}
          >
            {processing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span className="font-roboto font-semibold text-base">
                  Saving...
                </span>
              </>
            ) : isUploading ? (
              <span className="font-roboto font-semibold text-base">
                Uploading
              </span>
            ) : (
              <span className="font-roboto font-semibold text-base">Next</span>
            )}
          </Button>
        </div>

        <div className="flex flex-row items-center justify-center space-x-2 ">
          {error ? (
            <>
              <ShieldAlert className="h-3 w-3" />
              <span className="text-xs text-red-600">{error}</span>
            </>
          ) : (
            <>
              <LockClosedIcon className="h-3 w-3" />
              <span className="text-xs text-[#2CD673]">
                We use industry-standard encryption to protect your data
              </span>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default UploadSpecificCert1;
